import { CollapseButton } from "@msys/ui";
import { Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { isDescendant } from "react-sortable-tree";
import {
  color,
  cssValue,
  media,
  size,
} from "../../../common/MuiThemeProvider.js";
import { isItemGreyedOut } from "../../features/doc-items/helpers.js";
import { getSubcontractPath } from "../../features/quotes/helpers.js";
import { transientOptions } from "../../styles.js";
import { TreeSelectedIndicator } from "../../trees/components/TreeSelectedIndicator.js";
import { getItemChildren } from "../../trees/helpers.js";
import {
  createRecursiveTryFocusElementFunction,
  isInTestMode,
} from "../../utils.js";
import {
  NodeRendererProps,
  VirtualItem,
  VirtualTreeItemContext,
} from "../types.js";

export const VirtualItemNodeRenderer = <T extends VirtualItem>({
  node,
  canDrag = false,
  toggleChildrenVisibility,
  parentNode,
  draggedNode,
  canDrop = false,
  connectDragSource,
  connectDragPreview,
  didDrop,
  isDragging,
  path,
  treeIndex,
  items,
  allItems,
  rootItem,
  inputComponent: InputComponent,
  getRowAnnotation,
  rootItemComponent: RootItemComponent,
  itemComponent: ItemComponent,
  selectedItemId,
  lastSelectedItemId,
  showExpandButton,
}: NodeRendererProps<T> & VirtualTreeItemContext<T>) => {
  const isDraggedDescendant = draggedNode && isDescendant(draggedNode, node);
  const isLandingPadActive = !didDrop && isDragging;

  const resetFocus = React.useMemo(
    () =>
      createRecursiveTryFocusElementFunction(
        `input#msys-tree-input-${node.item.type}-${node.item.id}:enabled`
      ),
    [node.item.id, node.item.type]
  );

  const input = node.isInput ? (
    InputComponent ? (
      <InputComponent
        key={`msys-tree-input-${node.item.type}-${node.item.id}`}
        id={`msys-tree-input-${node.item.type}-${node.item.id}`}
        item={node.item}
        nodeRank={node.rank}
        itemChildren={getItemChildren(node.item, allItems ?? items)}
        isRootItem={node.item.isRootItem}
        autoFocus={false} // doesn't work well with virtual tree
        onAfterItemCreated={
          //  auto focus does not play nicely with e2e automated tests
          //  the e2e tests will set this sessionStorage as a temporary solution
          //  the positioned (add above/below) input is re-anchored + focused by
          //  the view after creation, so skip the bottom-focusing resetFocus here
          isInTestMode() || node.isAdditionalInput ? undefined : resetFocus
        }
      />
    ) : null
  ) : null;

  const component = !node.isInput ? (
    <div
      className="rst__row"
      style={{
        opacity: isDraggedDescendant ? 0.5 : 1,
      }}
    >
      {node.item.isRootItem && RootItemComponent ? (
        <RootItemComponent
          item={node.item}
          allItems={allItems ?? items}
          isRootItem={node.item.isRootItem}
          depth={0}
          parentItem={(parentNode?.item ?? rootItem)!}
          isGreyedOut={false} // already used it
          isHidden={node.item.isHidden}
          isPriceHidden={node.item.isPriceHidden}
          subcontractPath={getSubcontractPath(node.item)}
        />
      ) : ItemComponent ? (
        <ItemComponent
          item={node.item}
          allItems={allItems ?? items}
          isRootItem={node.item.isRootItem}
          depth={RootItemComponent ? path.length - 1 : path.length}
          parentItem={(parentNode?.item ?? rootItem)!}
          isGreyedOut={false} // already used it
          isHidden={node.item.isHidden}
          isPriceHidden={node.item.isPriceHidden}
          subcontractPath={getSubcontractPath(node.item)}
        />
      ) : null}
    </div>
  ) : null;

  const toggleButton = !node.isInput ? (
    toggleChildrenVisibility && showExpandButton(node.item) /* &&
    !node.item.isItemEliminated &&
    !node.item.decisionOptionElimination*/ ? (
      <StyledExpandWrapper className="rts__expandWrapper">
        <StyledExpandButtonWrapper>
          <CollapseButton
            aria-label={node.expanded ? "Collapse" : "Expand"}
            isExpanded={node.expanded}
            setIsExpanded={newExpanded =>
              toggleChildrenVisibility({
                node,
                path,
                treeIndex,
              })
            }
          />
        </StyledExpandButtonWrapper>
      </StyledExpandWrapper>
    ) : null
  ) : null;

  const annotation = getRowAnnotation?.(node) ?? null;

  const row = (
    <StyledContentWrapper
      $clickable={!node.isInput}
      $selected={node.selected}
      $selectedDescendant={
        !node.selected &&
        node.id !== selectedItemId &&
        node.id === lastSelectedItemId
      }
      $isInput={node.isInput}
      $isAnnotated={Boolean(annotation)}
      $isLandingPadActive={isLandingPadActive}
      $isLandingPadCancel={isLandingPadActive && !canDrop}
      $isGreyedOut={isItemGreyedOut(node.item)}
      $isPreselected={node.item.isParentDecisionPreselected}
    >
      {node.isInput ? (
        input
      ) : (
        <>
          {toggleButton}
          <StyledRowWrapper>
            {connectDragPreview(
              canDrag
                ? connectDragSource(component, { dropEffect: "copy" })
                : component
            )}
          </StyledRowWrapper>
        </>
      )}
      {node.selected && <TreeSelectedIndicator />}
    </StyledContentWrapper>
  );

  if (!annotation) return row;

  // The annotation is a *sibling* of the card, not a child — that is what puts it
  // outside the card surface. data-row-annotation marks the row so the tree's
  // scaffold CSS can correct the connector geometry for the taller row.
  return (
    <AnnotatedRow data-row-annotation="">
      <AnnotationText variant="caption" color="textSecondary">
        {annotation.text}
      </AnnotationText>
      {row}
    </AnnotatedRow>
  );
};

const AnnotatedRow = styled("div")`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 0;
`;

/**
 * MUI Typography rather than hand-rolled CSS: `variant="caption"` is the design
 * system's small-helper-text token. (An earlier version interpolated
 * theme.typography.caption — an object — into a template string, which emitted
 * "[object Object]" and silently applied no font styling at all.)
 */
const AnnotationText = styled(Typography)`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  /* Takes the space the row reserved beyond the pinned card, so any rounding
     lands here rather than changing the card's height. */
  flex: 1;
  min-height: 0;
  padding: 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledContentWrapper = styled(Paper, transientOptions)<{
  $selected: boolean;
  $selectedDescendant: boolean;
  $clickable: boolean;
  $isLandingPadActive: boolean;
  $isLandingPadCancel: boolean;
  $isGreyedOut: boolean;
  $isPreselected: boolean;
  $isInput: boolean;
  $isAnnotated: boolean;
}>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  background-color: ${({ $isGreyedOut, theme }) =>
    $isGreyedOut ? theme.palette.grey[100] : color.white};

  ${({ $selectedDescendant }) =>
    $selectedDescendant
      ? `
        box-shadow: ${cssValue.selectedDescendantBorderShadow};
      `
      : ""}
  ${({ $selected }) =>
    $selected
      ? `
        box-shadow: ${cssValue.selectedBorderShadow};
        @media (${media.minTablet}) {
          width: calc(100% - ${size.treeIndicatorWidth}px);
        }
      `
      : ""}

  ${({ $clickable, $selected, $isGreyedOut, theme }) =>
    $clickable && !$selected
      ? `
        &:hover {
          background-color: ${
            $isGreyedOut ? theme.palette.grey[200] : theme.palette.grey[50]
          };
        }
      `
      : ""}

  ${({ $isInput }) =>
    $isInput
      ? `
        min-width: 150px;
      `
      : ""}

  /* Under an annotation the card no longer owns the whole row: the annotation
     text sits above it as a sibling, so the card takes the remaining space. */
  /* Pin the card to exactly the height it has on every other row instead of
     letting flex distribute what is left over — the annotation absorbs any
     slack, so the card can never end up a pixel off from its neighbours. */
  ${({ $isAnnotated }) =>
    $isAnnotated
      ? `
        flex: 0 0 ${size.treeItemHeight}px;
        height: ${size.treeItemHeight}px;
      `
      : ""}

  ${({ $isLandingPadActive }) =>
    $isLandingPadActive
      ? `
          & > * {
            pointer-events: none;
          }
          cursor: move;
          background-color: ${color.blue7} !important;
        `
      : ""}

  ${({ $isLandingPadCancel }) =>
    $isLandingPadCancel
      ? `
          & > * {
            pointer-events: none;
          }
          cursor: move;
          background-color: ${color.redLight} !important;
        `
      : ""}

  .rst__row {
    height: 100%;
    width: 100%;
    white-space: nowrap;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  ${({ $isGreyedOut }) =>
    $isGreyedOut
      ? `
        .rts__expandWrapper > button {
          box-shadow: 0 0 0 1px ${color.white};
        }
      `
      : ""}
`;

const StyledExpandButtonWrapper = styled("div")({
  width: "34px",
  display: "flex",
  marginLeft: "-4px",
});

const StyledExpandWrapper = styled("div")(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  height: 100%;
  width: 14px;
  z-index: 5;

  & button {
    transform: translateX(-16px);
    & {
      background-color: ${theme.palette.grey[100]};
    }
    &:hover {
      background-color: ${theme.palette.grey[300]};
    }
    &:active {
      background-color: ${theme.palette.grey[400]};
    }
  }
`
);

const StyledRowWrapper = styled("div")(`
  height: ${size.treeItemHeight}px;
  box-sizing: border-box;
  flex-grow: 1;
  min-width: 0;
`);
